import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { questions, CompetenceArea } from "@/data/questions";
import { Answer } from "./Assessment";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Home, Download } from "lucide-react";

const Report = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { answers, userName, userEmail } = location.state as {
    answers: Answer[];
    userName: string;
    userEmail: string;
  } || {};

  useEffect(() => {
    if (!answers || !userName || !userEmail) {
      navigate("/");
    }
  }, [answers, userName, userEmail, navigate]);

  if (!answers) return null;

  // Calculate scores by competence area
  const calculateAreaScores = () => {
    const areas: CompetenceArea[] = [
      "Information & Data Literacy",
      "Communication & Collaboration",
      "Digital Content Creation",
      "Safety",
      "Problem Solving"
    ];

    return areas.map(area => {
      const areaQuestions = questions.filter(q => q.area === area);
      const areaAnswers = answers.filter(a =>
        areaQuestions.some(q => q.id === a.questionId)
      );

      const totalScore = areaAnswers.reduce((sum, a) => sum + a.value, 0);
      const maxScore = areaQuestions.length * 5;
      const percentage = (totalScore / maxScore) * 100;

      return {
        area: area,
        score: Math.round(percentage),
        level: percentage >= 80 ? "Advanced" : percentage >= 60 ? "Intermediate" : percentage >= 40 ? "Basic" : "Foundation"
      };
    });
  };

  const areaScores = calculateAreaScores();
  const overallScore = Math.round(
    areaScores.reduce((sum, area) => sum + area.score, 0) / areaScores.length
  );

  const radarData = areaScores.map(area => ({
    area: area.area.split(" ")[0],
    score: area.score
  }));

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-success";
    if (score >= 60) return "text-primary";
    if (score >= 40) return "text-accent";
    return "text-muted-foreground";
  };

  const getProgressBarColor = (score: number) => {
    if (score >= 80) return "bg-success";
    if (score >= 60) return "bg-primary";
    if (score >= 40) return "bg-accent";
    return "bg-muted";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Bilingual Instructions */}
        <Card className="p-8 mb-6 bg-gradient-to-r from-primary/5 to-accent/5 border-primary/30 shadow-lg">
          <div className="space-y-3">
            <p className="text-lg md:text-xl font-semibold text-foreground leading-relaxed">
              <strong className="text-primary text-xl md:text-2xl">Instructions:</strong> Review your comprehensive digital competence report. The report includes your overall score, detailed breakdown by competence area, and personalized recommendations.
            </p>
            <p className="text-lg md:text-xl font-semibold text-foreground font-tajawal leading-relaxed" dir="rtl">
              <strong className="text-primary text-xl md:text-2xl">التعليمات:</strong> راجع تقرير الكفاءة الرقمية الشامل الخاص بك. يتضمن التقرير درجتك الإجمالية، والتفصيل الكامل حسب مجال الكفاءة، والتوصيات الشخصية.
            </p>
          </div>
        </Card>
        {/* Header */}
        <Card className="p-8 mb-8 shadow-strong bg-gradient-to-r from-card to-primary/5">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">
                Digital Competence Report
              </h1>
              <p className="text-lg text-muted-foreground">
                Assessment completed by <span className="font-semibold text-foreground">{userName}</span>
              </p>
              <p className="text-sm text-muted-foreground">{userEmail}</p>
            </div>
            <Button variant="outline" onClick={() => navigate("/")} className="gap-2">
              <Home className="h-4 w-4" />
              Home
            </Button>
          </div>

          <div className="bg-card p-6 rounded-lg border-2 border-primary/20">
            <h2 className="text-sm font-medium text-muted-foreground mb-2">
              Overall Digital Competence Score
            </h2>
            <div className="flex items-end gap-3">
              <span className={`text-6xl font-bold ${getScoreColor(overallScore)}`}>
                {overallScore}
              </span>
              <span className="text-3xl text-muted-foreground mb-2">/100</span>
            </div>
          </div>
        </Card>

        {/* Radar Chart */}
        <Card className="p-8 mb-8 shadow-medium">
          <h2 className="text-2xl font-bold text-foreground mb-6">
            Competence Areas Overview
          </h2>
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="hsl(var(--border))" />
              <PolarAngleAxis
                dataKey="area"
                tick={{ fill: "hsl(var(--foreground))", fontSize: 12 }}
              />
              <PolarRadiusAxis angle={90} domain={[0, 100]} />
              <Radar
                name="Score"
                dataKey="score"
                stroke="hsl(var(--primary))"
                fill="hsl(var(--primary))"
                fillOpacity={0.3}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
        </Card>

        {/* Detailed Breakdown */}
        <Card className="p-8 shadow-medium">
          <h2 className="text-2xl font-bold text-foreground mb-6">
            Detailed Competence Breakdown
          </h2>
          <div className="space-y-6">
            {areaScores.map((area, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-foreground">{area.area}</h3>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">{area.level}</span>
                    <span className={`text-xl font-bold ${getScoreColor(area.score)}`}>
                      {area.score}%
                    </span>
                  </div>
                </div>
                <div className="w-full bg-secondary rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full ${getProgressBarColor(area.score)} transition-all duration-1000 rounded-full`}
                    style={{ width: `${area.score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recommendations */}
        <Card className="p-8 mt-8 shadow-medium bg-gradient-to-br from-primary/5 to-accent/5">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Recommendations
          </h2>
          <div className="space-y-4 text-foreground">
            <p className="leading-relaxed">
              Based on your assessment results, here are some personalized recommendations to enhance your digital competence:
            </p>
            <ul className="space-y-2 ml-4">
              {areaScores
                .filter(area => area.score < 70)
                .map((area, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-primary font-bold">•</span>
                    <span>
                      Focus on improving your <strong>{area.area}</strong> skills through online courses and practical exercises.
                    </span>
                  </li>
                ))}
              {areaScores.every(area => area.score >= 70) && (
                <li className="flex items-start gap-2">
                  <span className="text-success font-bold">✓</span>
                  <span>
                    Excellent work! Your digital competence is well-developed across all areas.
                    Consider mentoring others or exploring advanced topics in your strongest areas.
                  </span>
                </li>
              )}
            </ul>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Report;
